import { isSeasonalFeaturedCollectionBlock } from './designForm.utils'
import { normalizeHeroValuesForEditing } from './heroValues'
import {
  SEASONAL_RECOMMENDATION_INTENT,
  SEASONAL_RECOMMENDATION_RESULT,
} from './seasonalRecommendation.constants'
import {
  buildSeasonalScheduleRecommendationTransition,
  resolveSeasonalPlanningYear,
} from './seasonalScheduleRecommendationTransition'

const clone = (value) => structuredClone(value)

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value ?? {}, key)

export function buildSeasonalRecommendationTransition({
  values,
  recommendation,
  seasonalTagId,
  seasonalTags = [],
  defaultDesign,
  intent = SEASONAL_RECOMMENDATION_INTENT.SHUFFLE,
  previousSeasonalTag = {},
  scheduleProfile = null,
  selectedYear,
}) {
  const planningYear = resolveSeasonalPlanningYear(selectedYear ?? values.seasonal?.selected_year)
  const isInitialSetup = intent === SEASONAL_RECOMMENDATION_INTENT.INITIAL_SETUP
  const isTimingOnly = intent === SEASONAL_RECOMMENDATION_INTENT.USE_DEFAULT_TIMING
  const seasonalTagChanged =
    isInitialSetup || intent === SEASONAL_RECOMMENDATION_INTENT.SEASONAL_TAG_CHANGE
  const next = clone(values)
  next.pages ??= {}
  next.pages['/'] ??= {}
  next.pages['/'].blocks ??= {}
  next.section_overrides ??= {}
  next.schedule ??= {}
  next.seasonal ??= {}

  const snapshot = {
    name: values.name,
    seasonal_tag_id: hasOwn(previousSeasonalTag, 'formValue')
      ? previousSeasonalTag.formValue
      : values.seasonal_tag_id,
    pages: clone(values.pages),
    section_overrides: clone(values.section_overrides),
    schedule: clone(values.schedule),
    seasonal: clone(values.seasonal),
  }
  const homeBlocks = next.pages['/'].blocks

  if (seasonalTagChanged) {
    next.seasonal.seasonal_tag_id = seasonalTagId
    next.seasonal.featured_collection_id = null
    next.seasonal.effective_featured_collection_id = null
    next.seasonal.featured_collection_mode = 'automatic'
    homeBlocks.main = (Array.isArray(homeBlocks.main) ? homeBlocks.main : []).map((block) =>
      isSeasonalFeaturedCollectionBlock(block)
        ? { ...block, collections: [{ collection_id: null }] }
        : block
    )
  }

  const revision = recommendation?.published_revision
  const hasPublishedRecommendation = Number(recommendation?.id) > 0 && Number(revision?.id) > 0
  if (isInitialSetup) next.section_overrides.featured_content = true

  if (hasPublishedRecommendation && !isTimingOnly) {
    const media = revision.media ?? null
    const heroItems = Array.isArray(homeBlocks.hero?.hero) ? [...homeBlocks.hero.hero] : []
    const currentHero = heroItems[0] ?? { is_enabled: true, props: {}, media: [] }
    heroItems[0] = {
      ...currentHero,
      props: normalizeHeroValuesForEditing({
        ...normalizeHeroValuesForEditing(currentHero.props),
        ...(revision.hero ?? {}),
      }),
      media: media ? [media] : [],
    }
    homeBlocks.hero = { ...(homeBlocks.hero ?? {}), hero: heroItems }
    next.section_overrides.hero = true
    Object.assign(next.seasonal, {
      seasonal_design_id: recommendation.id,
      seasonal_design: recommendation,
      hero_mode: 'reference',
      mark_reviewed: true,
      selected_year: planningYear,
    })
  } else if (seasonalTagChanged) {
    homeBlocks.hero = clone(defaultDesign?.pages?.['/']?.blocks?.hero ?? { hero: [] })
    next.section_overrides.hero = false
    Object.assign(next.seasonal, {
      seasonal_design_id: null,
      seasonal_design: null,
      hero_mode: 'reference',
    })
  }

  const scheduleTransition = buildSeasonalScheduleRecommendationTransition({
    schedule: next.schedule,
    seasonalTagId,
    seasonalTags,
    scheduleProfile,
    intent,
    planningYear,
  })
  next.schedule = scheduleTransition.schedule
  if (
    scheduleTransition.didConfigure &&
    scheduleTransition.seasonalTag &&
    !String(next.name ?? '').trim()
  ) {
    next.name = scheduleTransition.seasonalTag.label ?? scheduleTransition.seasonalTag.name
  }

  return {
    values: next,
    snapshot,
    changedFields: isTimingOnly
      ? ['schedule']
      : ['name', 'seasonal_tag_id', 'pages', 'section_overrides', 'schedule', 'seasonal'],
    didChangeValues:
      (isTimingOnly && scheduleTransition.automaticTimingAvailable) ||
      (!isTimingOnly && (hasPublishedRecommendation || seasonalTagChanged)),
    result:
      (isTimingOnly && scheduleTransition.automaticTimingAvailable) ||
      (!isTimingOnly && hasPublishedRecommendation)
        ? SEASONAL_RECOMMENDATION_RESULT.APPLIED
        : SEASONAL_RECOMMENDATION_RESULT.WAITING,
    health: {
      status: hasPublishedRecommendation ? 'ready' : 'waiting_for_content',
      messages: hasPublishedRecommendation
        ? []
        : ['No published seasonal designs are available yet.'],
    },
  }
}
